import React, { useState } from 'react';
import { 
  X, 
  Calendar, 
  BookOpen, 
  Flame, 
  Copy, 
  Share2, 
  Check,
  ChevronLeft,
  ChevronRight,
  Heart,
  User,
  Target,
  Compass
} from 'lucide-react';
import { retreatContent } from '../data/retreatContent';

// Helper to get all days
const getAllDays = () => {
  const days = [];
  retreatContent.weeks.forEach(week => {
    week.days.forEach(day => {
      days.push({ ...day, weekNumber: week.number, pillar: week.pillar });
    });
  });
  return days;
};

const pillarEmojis = {
  Relationship: '❤️',
  Identity: '🪞',
  Mission: '🎯',
  Purpose: '🧭'
};

const WeeklySummaryModal = ({ isOpen, onClose, dailyNotes, currentDay, retreatStartDate }) => {
  const [copied, setCopied] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0); // 0 = current week, -1 = last week, etc.
  
  const allDays = getAllDays();
  
  // Calculate which week we're viewing
  const getWeekDays = () => {
    if (!retreatStartDate || !currentDay) return [];
    
    // Determine which days are in the selected week
    const today = currentDay;
    const weekStart = Math.max(1, today - (today % 7 || 7) + 1 + (weekOffset * 7));
    const weekEnd = Math.min(30, weekStart + 6);
    
    const days = [];
    for (let d = weekStart; d <= weekEnd; d++) {
      const dayInfo = allDays.find(day => day.day === d);
      days.push({
        day: d,
        title: dayInfo?.title || `Day ${d}`,
        scripture: dayInfo?.scripture || '',
        pillar: dayInfo?.pillar || 'Relationship',
        note: dailyNotes[d] || '',
        hasNote: !!dailyNotes[d]?.trim()
      });
    }
    return days;
  };

  const weekDays = getWeekDays();
  const completedThisWeek = weekDays.filter(d => d.hasNote).length;
  const totalWordsThisWeek = weekDays.reduce((acc, d) => acc + (d.note?.split(/\s+/).length || 0), 0);
  
  // Get week number and pillar
  const getWeekInfo = () => {
    if (weekDays.length === 0) return { number: 1, pillar: 'Relationship' };
    const firstDay = weekDays[0].day;
    if (firstDay <= 7) return { number: 1, pillar: 'Relationship' };
    if (firstDay <= 14) return { number: 2, pillar: 'Identity' };
    if (firstDay <= 21) return { number: 3, pillar: 'Mission' };
    return { number: 4, pillar: 'Purpose' };
  };
  
  const weekInfo = getWeekInfo();
  const canGoBack = weekDays.length > 0 && weekDays[0].day > 1;
  const canGoForward = weekOffset < 0;

  // Generate shareable summary text
  const generateSummaryText = () => {
    const header = `📿 My Lenten Retreat - Week ${weekInfo.number} Summary\n`;
    const subheader = `${pillarEmojis[weekInfo.pillar]} Theme: ${weekInfo.pillar}\n\n`;
    
    const stats = `📊 Stats:\n• ${completedThisWeek}/${weekDays.length} days completed\n• ${totalWordsThisWeek} words written\n\n`;
    
    let reflections = `✍️ My Reflections:\n`;
    weekDays.forEach(day => {
      if (day.hasNote) {
        reflections += `\nDay ${day.day}: ${day.title}\n`;
        reflections += `📖 ${day.scripture}\n`;
        reflections += `"${day.note.substring(0, 200)}${day.note.length > 200 ? '...' : ''}"\n`;
      }
    });
    
    const footer = `\n---\n🙏 The RIMP Way - DMU Lenten Retreat\n${window.location.origin}`;
    
    return header + subheader + stats + reflections + footer;
  };

  const handleCopy = async () => {
    const text = generateSummaryText();
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleShare = async () => {
    const text = generateSummaryText();
    if (navigator.share) {
      try {
        await navigator.share({
          title: `My Lenten Retreat - Week ${weekInfo.number}`,
          text: text
        });
      } catch (err) {
        // User cancelled or error
        handleCopy();
      }
    } else {
      handleCopy();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10001] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-md w-full max-h-[90vh] overflow-hidden shadow-xl flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] p-5 text-white flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6" />
              <h2 className="font-heading text-lg font-bold">Weekly Summary</h2>
            </div>
            <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Week Navigator */}
          <div className="flex items-center justify-between mt-4">
            <button 
              onClick={() => setWeekOffset(weekOffset - 1)}
              disabled={!canGoBack}
              className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-30"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div className="text-center">
              <p className="font-semibold">Week {weekInfo.number}</p>
              <p className="text-white/70 text-sm">{weekInfo.pillar}</p>
            </div>
            <button 
              onClick={() => setWeekOffset(weekOffset + 1)}
              disabled={!canGoForward}
              className="p-2 hover:bg-white/20 rounded-full transition-colors disabled:opacity-30"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-rose-50 rounded-xl p-3 text-center border border-rose-100">
              <BookOpen className="w-5 h-5 text-rose-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-rose-600">{completedThisWeek}/{weekDays.length}</p>
              <p className="text-xs text-rose-500">Days Completed</p>
            </div>
            <div className="bg-amber-50 rounded-xl p-3 text-center border border-amber-100">
              <Flame className="w-5 h-5 text-amber-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-amber-600">{totalWordsThisWeek}</p>
              <p className="text-xs text-amber-500">Words Written</p>
            </div>
          </div>

          {/* Day-by-day */}
          <div className="space-y-2">
            <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">This Week's Reflections</p>
            {weekDays.map(day => (
              <div 
                key={day.day}
                className={`rounded-xl p-3 border ${day.hasNote ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-100'}`}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${day.hasNote ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'}`}>
                    {day.hasNote ? <Check className="w-4 h-4" /> : day.day}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-800 text-sm">Day {day.day}: {day.title}</p>
                    <p className="text-xs text-gray-500">{day.scripture}</p>
                    {day.hasNote && (
                      <p className="text-xs text-gray-600 mt-1 line-clamp-2 italic">"{day.note}"</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {completedThisWeek === 0 && (
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
              <p className="text-amber-800 text-sm">
                No reflections written this week yet. Start journaling to see your summary here!
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="p-4 bg-gray-50 border-t border-gray-100 flex gap-2 flex-shrink-0">
          <button
            onClick={handleCopy}
            className="flex-1 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button
            onClick={handleShare}
            className="flex-1 py-3 bg-[var(--primary)] text-white rounded-xl font-medium hover:bg-[var(--primary-dark)] transition-colors flex items-center justify-center gap-2"
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>
        </div>
      </div>
    </div>
  );
};

export default WeeklySummaryModal;
